<template>
  <div class="test-item-management">
    <el-card class="test-item-card">
      <div slot="header" class="card-header">
        <span>体测项目管理</span>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-container">
        <el-row :gutter="20">
          <el-col :span="14">
            <el-input v-model="filterForm.name" placeholder="输入项目名称"></el-input>
          </el-col>
          <el-col :span="10">
            <el-button type="primary" @click="fetchTestItems">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
            <el-button type="primary" @click="showAddTestItemDialog">添加项目</el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 项目表格 -->
      <el-table
        v-loading="loading"
        :data="testItems"
        border
        style="width: 100%"
        class="test-items-table"
        fit
        stripe
      >
        <el-table-column prop="id" label="项目id" min-width="80"></el-table-column>
        <el-table-column prop="name" label="项目名称" min-width="120"></el-table-column>
        <el-table-column prop="unit" label="计量单位" min-width="100"></el-table-column>
        <el-table-column label="男生评分标准" min-width="150">
          <template #default="scope">
            {{ scope.row.maleStandard || '查看标准' }}
          </template>
        </el-table-column>
        <el-table-column label="女生评分标准" min-width="150">
          <template #default="scope">
            {{ scope.row.femaleStandard || '查看标准' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="120">
          <template #default="scope">
            <el-button type="text" @click="showEditTestItemDialog(scope.row)">编辑</el-button>
            <el-button type="text" danger @click="deleteTestItem(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 添加/编辑项目对话框 -->
    <el-dialog v-model="testItemDialogVisible" :title="isEdit ? '编辑体测项目' : '添加体测项目'" width="600px">
      <el-form
        ref="testItemFormRef"
        :model="testItemForm"
        :rules="testItemRules"
        label-width="120px"
      >
        <el-form-item label="项目名称" prop="name">
          <el-input v-model="testItemForm.name" placeholder="请输入项目名称"></el-input>
        </el-form-item>
        <el-form-item label="计量单位" prop="unit">
          <el-input v-model="testItemForm.unit" placeholder="请输入计量单位"></el-input>
        </el-form-item>
        <el-form-item label="男生评分标准" prop="maleScoringStandard">
          <el-input
            v-model="testItemForm.maleScoringStandard"
            type="textarea"
            :rows="3"
            placeholder="请输入男生评分标准"
          ></el-input>
        </el-form-item>
        <el-form-item label="女生评分标准" prop="femaleScoringStandard">
          <el-input
            v-model="testItemForm.femaleScoringStandard"
            type="textarea"
            :rows="3"
            placeholder="请输入女生评分标准"
          ></el-input>
        </el-form-item>

      </el-form>
      <template #footer>
        <el-button @click="testItemDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTestItemForm">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

// 加载状态
const loading = ref(false);

// 筛选条件
const filterForm = reactive({
  name: ''
});

// 表格数据
const testItems = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 对话框状态
const testItemDialogVisible = ref(false);
const isEdit = ref(false);

// 表单引用
const testItemFormRef = ref(null);

// 项目表单 - 确保使用正确的响应式对象
const testItemForm = reactive({
  id: '',
  name: '',
  unit: '',
  maleScoringStandard: '',
  femaleScoringStandard: ''
});

// 项目表单验证规则
const testItemRules = {
    name: [
      { required: true, message: '请输入项目名称', trigger: 'blur' },
      { min: 2, max: 50, message: '项目名称长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    unit: [
      { required: true, message: '请输入计量单位', trigger: 'blur' },
      { min: 1, max: 10, message: '计量单位长度在 1 到 10 个字符', trigger: 'blur' }
    ],
    maleScoringStandard: [
      { required: true, message: '请输入男生评分标准', trigger: 'blur' }
    ],
    femaleScoringStandard: [
      { required: true, message: '请输入女生评分标准', trigger: 'blur' }
    ]
  };

// 获取类别文本已移除，不再需要
// 获取类别样式已移除，不再需要

// 获取项目列表
const fetchTestItems = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      size: pageSize.value,
      name: filterForm.name || undefined
    };

    const response = await axios.get('/admin/test-items/list', { params });
    console.log('后端返回的数据:', response.data.data); // 添加调试日志
    testItems.value = response.data.data;
    total.value = response.data.total;
  } catch (error) {
    console.error('获取体测项目列表失败:', error);
    ElMessage.error('获取体测项目列表失败');
  } finally {
    loading.value = false;
  }
};

// 重置筛选
const resetFilter = () => {
  filterForm.name = '';
  currentPage.value = 1;
  fetchTestItems();
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
  currentPage.value = 1;
  fetchTestItems();
};

const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchTestItems();
};

// 显示添加项目对话框
const showAddTestItemDialog = () => {
  isEdit.value = false;
  testItemForm.id = '';
  testItemForm.name = '';
  testItemForm.unit = '';
  testItemForm.maleScoringStandard = '';
  testItemForm.femaleScoringStandard = '';
  testItemDialogVisible.value = true;
};

// 显示编辑项目对话框
const showEditTestItemDialog = (item) => {
  console.log('编辑项目数据:', item); // 添加调试日志
  isEdit.value = true;
  
  // 先重置表单，然后再设置数据
  if (testItemFormRef.value) {
    testItemFormRef.value.resetFields();
  }
  
  // 强制清空并重新赋值，确保响应式更新
  Object.assign(testItemForm, {
    id: item.id || '',
    name: item.name || '',
    unit: item.unit || '',
    maleScoringStandard: item.maleStandard || '',
    femaleScoringStandard: item.femaleStandard || ''
  });
  
  console.log('设置到表单的数据:', testItemForm); // 显示设置到表单的数据
  
  // 先设置数据，再显示对话框
  testItemDialogVisible.value = true;
};


// 提交项目表单
const submitTestItemForm = async () => {
  try {
    await testItemFormRef.value.validate();

    if (isEdit.value) {
      // 编辑项目
      // 构造与后端TestItem实体匹配的数据结构
      const submitData = {
        name: testItemForm.name,
        unit: testItemForm.unit,
        maleStandard: testItemForm.maleScoringStandard,
        femaleStandard: testItemForm.femaleScoringStandard
      };
      
      const response = await axios.put(`/admin/test-items/${testItemForm.id}`, submitData);
      if (response.data.code === 200) {
        ElMessage.success('体测项目编辑成功');
      }
    } else {
      // 添加项目
      // 构造与后端TestItem实体匹配的数据结构
      const submitData = {
        name: testItemForm.name,
        unit: testItemForm.unit,
        maleStandard: testItemForm.maleScoringStandard,
        femaleStandard: testItemForm.femaleScoringStandard
      };
      
      const response = await axios.post('/admin/test-items', submitData);
      if (response.data.code === 200) {
        ElMessage.success('体测项目添加成功');
      }
    }

    testItemDialogVisible.value = false;
    fetchTestItems();
  } catch (error) {
    console.error('提交体测项目表单失败:', error);
    ElMessage.error(isEdit.value ? '体测项目编辑失败' : '体测项目添加失败');
  }
};

// 更改项目状态
const handleStatusChange = async (item) => {
  try {
    const newStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    // 使用修改后的status端点，构造与后端TestItem实体匹配的数据结构
    const submitData = {
      itemName: item.name,
      unit: item.unit,
      maleStandard: item.maleScoringStandard,
      femaleStandard: item.femaleScoringStandard,
      status: newStatus
    };
    
    const response = await axios.put(`/admin/test-items/${item.id}/status`, submitData);
    if (response.data.code === 200) {
      ElMessage.success(`体测项目已${newStatus === 'ACTIVE' ? '启用' : '禁用'}`);
      item.status = newStatus;
    } else {
      ElMessage.error('状态更新失败');
    }
  } catch (error) {
    console.error('更改体测项目状态失败:', error);
    ElMessage.error('状态更新失败');
  }
};

// 删除项目
const deleteTestItem = async (item) => {
  try {
    await ElMessageBox.confirm('确定要删除该体测项目吗？', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });

    const response = await axios.delete(`/admin/test-items/${item.id}`);
    if (response.data.code === 200) {
      ElMessage.success('体测项目删除成功');
      fetchTestItems();
    } else {
      ElMessage.error('体测项目删除失败');
    }
  } catch (error) {
    if (error === 'cancel') return;
    console.error('删除体测项目失败:', error);
    ElMessage.error('体测项目删除失败');
  }
};

onMounted(() => {
  fetchTestItems();
});
</script>

<style scoped>
.test-item-management {
  padding: 10px 0;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

.filter-container {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.test-items-table {
  margin-bottom: 20px;
  width: 100%;
  display: table;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}
</style>